import { useState } from "react";
import { MessageCircle, AlertCircle, CalendarCheck, Megaphone, Tags, FileDown, Search, X, SlidersHorizontal, ChevronDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { CONVERSATIONS } from "@/constants/testIds";
import { useCompactLayout } from "@/hooks/use-compact-layout";
import { CONVERSATION_STATUS_LABELS, CONVERSATION_STATUS_COLORS, formatConversationTime, conversationTitle, CONVERSATION_STAGES, CONVERSATION_TAGS, stageLabel, stageColor, tagLabel } from "@/lib/conversationsConstants";

const FILTER_SELECT = "h-8 w-full min-w-0 rounded-md border bg-white px-1.5 text-[11px] focus:outline-none focus:ring-1 focus:ring-brand";
// Fix AI (2026-10-23): en el celular los filtros son mas altos (para el dedo)
const FILTER_SELECT_TOUCH = "h-10 w-full min-w-0 rounded-md border bg-white px-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand";

/** Lista de conversaciones (columna izquierda del inbox). */
export default function ConversationList({ conversations, selectedId, onSelect, filter, onFilterChange, unreadOnly = false, onUnreadOnlyChange, services = [], classFilter, onClassFilterChange, tagList = CONVERSATION_TAGS, onManageList, onExport, search = "", onSearchChange, stageList = CONVERSATION_STAGES }) {
  // Fix AI (2026-10-23): en el celular la lista ocupa todo el ancho, se desplaza junto con la columna
  // y los filtros por etapa, etiqueta y servicio quedan plegados detras de "Filtros".
  const compact = useCompactLayout();
  const [showFilters, setShowFilters] = useState(false);
  const activeFilters = ["stage", "tag", "service"].filter((k) => classFilter[k] !== "all").length;
  const filtersHidden = compact && !showFilters;
  const filters = [
    { key: "all", label: "Todas", testId: CONVERSATIONS.filterAll },
    { key: "escalated", label: "Escaladas", testId: CONVERSATIONS.filterEscalated },
    { key: "bot", label: "Bot", testId: CONVERSATIONS.filterBot },
    { key: "resolved", label: "Resueltas", testId: CONVERSATIONS.filterResolved },
  ];

  return (
    <div className={`flex flex-col bg-white border border-border rounded-xl ${compact ? "" : "h-full overflow-hidden"}`}>
      {/* Fix AI (2026-10-23): en el celular los filtros pasan a otra linea en vez de quedar tapados */}
      <div className={`flex gap-1.5 p-3 border-b border-border ${compact ? "flex-wrap" : "overflow-x-auto"}`}>
        {filters.map((f) => (
          <button
            key={f.key}
            data-testid={f.testId}
            onClick={() => onFilterChange(f.key)}
            className={`px-3 ${compact ? "py-2.5" : "py-1.5"} rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
              filter === f.key ? "bg-brand text-white" : "bg-muted text-muted-foreground hover:bg-brand-light"
            }`}
          >
            {f.label}
          </button>
        ))}
        {/* Fix BG (2026-11-04): ver solo los chats con mensajes sin leer (se combina con los filtros de al lado) */}
        {onUnreadOnlyChange && (
          <button
            type="button"
            data-testid={CONVERSATIONS.filterUnread}
            aria-pressed={unreadOnly}
            onClick={() => onUnreadOnlyChange(!unreadOnly)}
            className={`px-3 ${compact ? "py-2.5" : "py-1.5"} rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
              unreadOnly ? "bg-[#25D366] text-white" : "bg-muted text-muted-foreground hover:bg-brand-light"
            }`}
          >
            No leídos
          </button>
        )}
      </div>
      {/* Fix AF (2026-10-19): buscar un chat por telefono (o nombre). */}
      <div className={`px-3 pt-2 ${compact ? "pb-2" : ""}`}>
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            inputMode="search"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Buscar por teléfono o nombre"
            className={`${compact ? "h-10" : "h-8"} w-full rounded-md border border-border bg-white pl-8 pr-9 text-xs focus:outline-none focus:ring-1 focus:ring-brand`}
            data-testid={CONVERSATIONS.searchInput}
          />
          {search && (
            <button type="button" onClick={() => onSearchChange("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label="Borrar búsqueda" data-testid={CONVERSATIONS.searchClear}>
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
      {compact && (
        <button type="button" onClick={() => setShowFilters((v) => !v)} aria-expanded={showFilters} className="flex items-center justify-between gap-2 px-3 h-10 border-y border-border text-sm text-muted-foreground" data-testid={CONVERSATIONS.filtersToggle}>
          <span className="inline-flex items-center gap-2"><SlidersHorizontal className="w-4 h-4" /> Filtros{activeFilters > 0 ? ` (${activeFilters})` : ""}</span>
          <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? "rotate-180" : ""}`} />
        </button>
      )}
      {/* Fix AD (2026-10-15): filtros por etapa, etiqueta y servicio de interes. */}
      <div className={`grid ${compact ? "grid-cols-1" : "grid-cols-3"} gap-1.5 px-3 py-2 border-b border-border ${filtersHidden ? "hidden" : ""}`}>
        {[
          { key: "stage", label: "Etapa", options: stageList.map((s) => ({ value: s.key, label: s.hidden ? `${s.label} (oculta)` : s.label })), testId: CONVERSATIONS.filterStage },
          { key: "tag", label: "Etiqueta", options: tagList.map((t) => ({ value: t.key, label: t.hidden ? `${t.label} (oculta)` : t.label })), testId: CONVERSATIONS.filterTag },
          { key: "service", label: "Servicio", options: services.map((s) => ({ value: s.id, label: s.is_active === false ? `${s.name} (oculto)` : s.name })), testId: CONVERSATIONS.filterService },
        ].map((f) => (
          <select
            key={f.key}
            value={classFilter[f.key]}
            onChange={(e) => onClassFilterChange({ ...classFilter, [f.key]: e.target.value })}
            className={`${compact ? FILTER_SELECT_TOUCH : FILTER_SELECT} ${classFilter[f.key] !== "all" ? "border-brand text-brand" : "border-border text-muted-foreground"}`}
            data-testid={f.testId}
          >
            <option value="all">{f.label}</option>
            {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        ))}
      </div>
      {/* Fix AE (2026-10-19): administrar las etiquetas y bajar en Excel lo que muestra la lista */}
      <div className={`flex gap-1.5 px-3 py-1.5 border-b border-border ${filtersHidden ? "hidden" : ""}`}>
        {/* Fix AG (2026-10-21): un menu para las tres listas que edita el equipo */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className={`flex-1 inline-flex items-center justify-center gap-1 ${compact ? "h-10 text-sm" : "h-7 text-[11px]"} rounded-md text-muted-foreground hover:bg-muted hover:text-foreground`} data-testid={CONVERSATIONS.manageListsButton}>
              <Tags className="w-3.5 h-3.5" /> Administrar listas
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-48">
            <DropdownMenuItem onSelect={() => onManageList("tags")} data-testid={CONVERSATIONS.manageTagsButton}>Etiquetas</DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onManageList("stages")} data-testid={CONVERSATIONS.manageStagesButton}>Etapas</DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onManageList("interests")} data-testid={CONVERSATIONS.manageInterestsButton}>Servicios de interés</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <button type="button" onClick={onExport} className={`flex-1 inline-flex items-center justify-center gap-1 ${compact ? "h-10 text-sm" : "h-7 text-[11px]"} rounded-md text-muted-foreground hover:bg-muted hover:text-foreground`} data-testid={CONVERSATIONS.exportButton}>
          <FileDown className="w-3.5 h-3.5" /> Descargar Excel
        </button>
      </div>
      <div className={compact ? "" : "flex-1 overflow-y-auto"} data-testid={CONVERSATIONS.list}>
        {conversations.length === 0 ? (
          <div className="p-8 text-center" data-testid={CONVERSATIONS.listEmptyState}>
            <MessageCircle className="w-8 h-8 text-muted-foreground/40 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">{search.trim() ? "No hay chats que coincidan con la búsqueda." : unreadOnly ? "No hay chats sin leer." : "No hay conversaciones en esta categoria."}</p>
          </div>
        ) : (
          conversations.map((c) => {
            // Fix BG (2026-11-04): mensajes del cliente sin leer (el chat abierto cuenta como leido)
            const unread = selectedId === c.id ? 0 : c.unread_count || 0;
            return (
            <button
              key={c.id}
              onClick={() => onSelect(c.id)}
              data-testid={`${CONVERSATIONS.rowPrefix}${c.id}`}
              className={`w-full text-left px-4 py-3 border-b border-border/60 transition-colors flex items-start gap-2 ${
                selectedId === c.id ? "bg-brand-light" : "hover:bg-muted/50"
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className={`text-sm text-foreground truncate ${unread ? "font-semibold" : "font-medium"}`}>{conversationTitle(c)}</p>
                  <span className="flex items-center gap-1.5 shrink-0">
                    <span className={`text-[11px] ${unread ? "text-[#1DA851] font-medium" : "text-muted-foreground"}`}>{formatConversationTime(c.last_message_at)}</span>
                    {unread > 0 && (
                      <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-[#25D366] text-white text-[10px] font-semibold flex items-center justify-center" data-testid={`${CONVERSATIONS.unreadBadgePrefix}${c.id}`}>
                        {unread > 99 ? "99+" : unread}
                      </span>
                    )}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground truncate mt-0.5">{c.last_message_preview || "Sin mensajes"}</p>
                <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                  <Badge className={`${CONVERSATION_STATUS_COLORS[c.status]} border-none font-normal text-[10px]`} data-testid={`${CONVERSATIONS.statusBadgePrefix}${c.id}`}>
                    {CONVERSATION_STATUS_LABELS[c.status]}
                  </Badge>
                  {/* Fix AD (2026-10-15): etapa comercial del chat. */}
                  <Badge className={`${stageColor(c.stage)} border-none font-normal text-[10px]`} data-testid={`${CONVERSATIONS.stageBadgePrefix}${c.id}`}>
                    {stageLabel(c.stage, stageList)}
                  </Badge>
                  {c.booked_appointment && (
                    <Badge className="bg-[#E3EDE1] text-[#5B7A54] border-none font-normal text-[10px] gap-1" data-testid={`${CONVERSATIONS.bookedAppointmentBadgePrefix}${c.id}`}>
                      <CalendarCheck className="w-2.5 h-2.5" /> Turno agendado
                    </Badge>
                  )}
                  {c.ad_referral && (
                    // Fix AB (2026-10-15): el cliente llego desde un anuncio.
                    <Badge className="bg-[#E7E9F4] text-[#4F5B93] border-none font-normal text-[10px] gap-1 max-w-full" title={c.ad_referral.headline || ""} data-testid={`${CONVERSATIONS.adBadgePrefix}${c.id}`}>
                      <Megaphone className="w-2.5 h-2.5 shrink-0" /> <span className="truncate">{c.ad_referral.source_type === "post" ? "Publicación" : "Anuncio"}{c.ad_referral.headline ? `: ${c.ad_referral.headline}` : ""}</span>
                    </Badge>
                  )}
                  {(c.tags || []).map((t) => (
                    <span key={t} className="text-[10px] px-1.5 py-0.5 rounded-full border border-border text-muted-foreground" data-testid={`${CONVERSATIONS.rowTagPrefix}${c.id}-${t}`}>
                      {tagLabel(t, tagList)}
                    </span>
                  ))}
                </div>
              </div>
              {c.needs_attention && (
                <AlertCircle className="w-4 h-4 text-[#B5793A] shrink-0 mt-1" data-testid={`${CONVERSATIONS.attentionDotPrefix}${c.id}`} />
              )}
            </button>
            );
          })
        )}
      </div>
    </div>
  );
}
